import { brandService } from './brand';

export interface ExternalApp {
  /** Doubles as the `mainView` value while this app is open. */
  id: string;
  label: string;
  /** Where the app lives on its deployed host. */
  url: string;
  /**
   * Served from this machine instead, used only while *this* app is itself opened on localhost —
   * so a locally running dashboard is framed without ever shipping a localhost URL to a deploy.
   */
  localUrl?: string;
  /** Brand ids that should see this app (empty = every brand). */
  brands: string[];
  /** Render in the main nav beside the device views instead of the "other apps" block. */
  pinned?: boolean;
  /** i18n key for the label; `label` is the fallback when there is none. */
  labelKey?: string;
}

/**
 * Sibling apps deployed next to this one. Rendered in the browser, so it must
 * stay non-secret (same rule as the brand profile — see docs/TROUBLESHOOTING.md §5).
 *
 * An app can only be embedded if its origin does NOT send `X-Frame-Options` or a
 * CSP `frame-ancestors` — verified for horse-dance-ai.feelingss.ai.
 */
/** Ids of the sibling apps, for the view unions in `App.tsx` / `Sidebar.tsx`. Extend when adding an entry. */
export type ExternalAppId = 'horseDance' | 'aiotDashboard' | 'healthMonitor';

export const EXTERNAL_APPS: ExternalApp[] = [
  {
    id: 'horseDance',
    label: 'Equine Dressage AI',
    labelKey: 'externalHorseDance',
    url: 'https://horse-dance-ai.feelingss.ai/',
    // Visible on feelingss (its own subdomain) and on mednnect for local testing;
    // narrow this list if the mednnect deployment should not link out.
    brands: ['feelingss', 'mednnect'],
  },
  {
    // Deployed URL: https://aiot.feelingss.ai/ — hosted on the *same registrable domain* as this app
    // on purpose: a same-site iframe sends the dashboard's Supabase SSR session cookie, so its login
    // holds inside the frame. A cross-site embed (e.g. ai.mednnect.com framing a feelingss domain)
    // cannot, and would also show another brand's chrome.
    // Opened on localhost we frame the dashboard's own dev server instead, which is same-site for the
    // same reason (the port does not change the site). That switch is in resolveExternalAppUrl below,
    // so the deployed URL here is what ships.
    id: 'aiotDashboard',
    label: 'AI Camera Dashboard',
    labelKey: 'externalAiotDashboard',
    url: 'https://aiot.feelingss.ai/',
    localUrl: 'http://localhost:3000/recordings',
    // Sits in the main nav beside "Sensor Dashboard" (the app's own sensor/live view), so the two
    // read as a pair: sensors here, camera clips/visits there.
    pinned: true,
    // feelingss first: the host shares this app's registrable domain, so its login holds in-frame.
    // mednnect is here for LOCAL testing only (the local brand is mednnect) — on ai.mednnect.com this
    // would frame a feelingss domain: cross-site, so the login cannot hold and another brand's chrome
    // shows. Narrow this back to ['feelingss'] unless a mednnect-domain host is deployed.
    brands: ['feelingss', 'mednnect'],
  },
  {
    // Not another deployment: the cat pages are this app's own second entry point
    // (`poopfect.html`), served from this origin. Framing it keeps the shell — the
    // sidebar, the Back button, "open in a new tab" — around a page that fills a
    // window by design, instead of navigating away from everything else.
    // No `localUrl`: it is relative and the same on every host and in dev.
    id: 'healthMonitor',
    label: 'Health Monitor',
    labelKey: 'catHealthMonitor',
    url: '/poopfect',
    brands: [],
  },
];

/** True while this app itself is opened from a local machine rather than its deployed host. */
export function onLocalOrigin(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0" || host === "[::1]";
}

/** The URL to frame right now: the local one only while we are running locally ourselves. */
export function resolveExternalAppUrl(app: ExternalApp): string {
  return onLocalOrigin() && app.localUrl ? app.localUrl : app.url;
}

export function getVisibleExternalApps(): ExternalApp[] {
  const brandId = brandService.getProfile()?.id || '';
  return EXTERNAL_APPS.filter((app) => app.brands.length === 0 || app.brands.includes(brandId)).map(
    (app) => ({ ...app, url: resolveExternalAppUrl(app) }),
  );
}

export function getExternalApp(id: string): ExternalApp | undefined {
  const app = EXTERNAL_APPS.find((app) => app.id === id);
  return app ? { ...app, url: resolveExternalAppUrl(app) } : undefined;
}
